import { useCallback, useEffect, useRef, useState } from 'react';
import type { AssistantExtConfirmDTO, AssistantPendingDTO, AssistantSseEvent } from '@contracts/assistant';
import type { Id } from '@contracts/common';
import { extErrorText, onCardRefusal } from './ext-card-labels';
import type { ExtOutcome } from './ExtConfirmCard';
import type { ActTap } from './useTurn';

/**
 * One external card whose Xác nhận was pressed: the card as it was, what became of it, and the stored message it was
 * confirmed under (layer review F1: once settled it stays in that turn, above the answer its confirm brought).
 */
export type ExtEntry = { card: AssistantPendingDTO; outcome: ExtOutcome; after: Id | null };

type StepDone = Extract<AssistantSseEvent, { type: 'tool_done' }>['data'];
const fromStep = (d: StepDone): ExtOutcome => (d.ok ? { state: 'executed' }
  : { state: 'failed', code: d.errorCode ?? 'EXT_UNKNOWN' });
const fromJson = (data: unknown): ExtOutcome | null => {
  const d = data as Partial<AssistantExtConfirmDTO> | undefined;
  if (d?.status === 'sending' || d?.status === 'executed') return { state: d.status };
  if (d?.status === 'failed') return { state: 'failed', code: d.errorCode ?? 'EXT_UNKNOWN' };
  return null;
};

/**
 * S6 §7.3, §7.7 (D47): an external card stays on screen through its Xác nhận — "Đang gửi…", then "Đã gửi", "Không gửi
 * được" or "Không chắc đã gửi" — where an internal card leaves at once. The answer is the confirm's own: the resumed
 * turn's `tool_done` for the card's call, or the JSON `{ status }`. The call is the live stream's for this card, else
 * the one the card carries (G3 F1: a reloaded card, an edited one). Never a step's name: a card with no call (a row
 * stored before 026's `call_id`) gets no answer from the stream and leaves, the trail and Hana's reply saying the rest.
 * A refusal that leaves the card pending is said on the card.
 */
export function useExtConfirm(o: {
  conversationId: Id | null;
  act: (actionId: Id, kind: 'confirm', callId?: string | null, tap?: ActTap) => Promise<'sent' | 'refused'>;
  callOf: (actionId: Id) => string | null;
  /** The live turn's `known`, new per turn, and whether the person wrote it (a message, not a confirm). */
  liveKnown: readonly Id[] | undefined; liveAsked: boolean;
}): { entries: ReadonlyMap<Id, ExtEntry>; confirm: (card: AssistantPendingDTO, after: Id | null) => void } {
  const { conversationId, act, callOf, liveKnown, liveAsked } = o;
  const [entries, setEntries] = useState<ReadonlyMap<Id, ExtEntry>>(() => new Map());
  const out = useRef(new Set<Id>());
  const put = useCallback((card: AssistantPendingDTO, after: Id | null, outcome: ExtOutcome | null) => setEntries((m) => {
    const next = new Map(m);
    if (outcome === null) next.delete(card.actionId);
    else next.set(card.actionId, { card, outcome, after });
    return next;
  }), []);

  // Another conversation: its cards are not this one's.
  useEffect(() => { setEntries(new Map()); }, [conversationId]);
  // A new message: a settled card stays behind with its turn, not under the new one.
  useEffect(() => {
    if (liveKnown === undefined || !liveAsked) return;
    setEntries((m) => new Map([...m].filter(([id, e]) => e.outcome.state === 'sending' || out.current.has(id))));
  }, [liveKnown, liveAsked]);

  const confirm = useCallback((card: AssistantPendingDTO, after: Id | null) => {
    const ext = card.external;
    if (ext === undefined || out.current.has(card.actionId)) return;   // one confirm in flight per card
    out.current.add(card.actionId);
    put(card, after, { state: 'sending' });
    const call = callOf(card.actionId) ?? card.callId ?? null;
    let step: ExtOutcome | null = null;   // the card's own call, as the resumed turn ended it
    let answer: ExtOutcome | null = null;
    const tap: ActTap = {
      onEvent(e) {
        if (call === null || e.type !== 'tool_done' || e.data.callId !== call) return;
        // Said at once, while Hana's reply still streams.
        step = fromStep(e.data);
        put(card, after, step);
      },
      onJson(data) { answer = fromJson(data); },
      onRefused(r) {
        if (!onCardRefusal(r.code)) return false;
        answer = { state: 'refused', message: extErrorText(r.code, ext.serverName, r.message) };
        return true;
      },
    };
    void act(card.actionId, 'confirm', call, tap).then(() => {
      put(card, after, answer ?? step);
    }).finally(() => { out.current.delete(card.actionId); });
  }, [act, callOf, put]);

  return { entries, confirm };
}
